import { Box, ButtonBase } from "@mui/material";
import colors from "../../config/colors";
import { colorPalette } from "../../config/colorPalette";

// MUI icons sit in a padded box, so these are a little larger than Figma's 12px / 10px glyphs.
const iconSx = (size) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: size,
  "& svg": { fontSize: "inherit", width: "1em", height: "1em" },
});

const STATES = {
  default: {
    backgroundColor: colors.white,
    borderColor: colorPalette.mercuryAlpha[18],
    color: colorPalette.mercury[950],
    fontWeight: 400,
    countBg: colorPalette.brand[700],
    countColor: colors.white,
  },
  selected: {
    backgroundColor: colorPalette.brand[100],
    borderColor: colorPalette.brand[700],
    color: colorPalette.brand[700],
    fontWeight: 500,
    countBg: colorPalette.brand[700],
    countColor: colors.white,
  },
  // selected + solid: filled in the danger colour, for yes / no style choices
  selectedSolid: {
    backgroundColor: colorPalette.red[600],
    borderColor: colorPalette.red[600],
    color: colors.white,
    fontWeight: 500,
    countBg: colors.white,
    countColor: colorPalette.red[600],
  },
  disabled: {
    backgroundColor: colorPalette.mercuryAlpha[5],
    borderColor: colorPalette.mercuryAlpha[8],
    color: colorPalette.mercuryAlpha[28],
    fontWeight: 400,
    countBg: colorPalette.mercuryAlpha[12],
    countColor: colorPalette.mercuryAlpha[28],
  },
  // disabled + selected: still greyed, but darker, so a read-only answer stays visible
  disabledSelected: {
    backgroundColor: colorPalette.mercuryAlpha[12],
    borderColor: colorPalette.mercuryAlpha[28],
    color: colorPalette.mercuryAlpha[65],
    fontWeight: 500,
    countBg: colorPalette.mercuryAlpha[28],
    countColor: colors.white,
  },
};

/**
 * Chip
 *
 * Props:
 * - children: label
 * - selected: selected look (orange, medium weight)
 * - solid: with selected, fills the chip in the danger colour with white text
 * - disabled: disabled look (and a disabled button when onClick is set); with selected, a darker grey
 * - startIcon / endIcon: React nodes (filled @mui/icons-material icons)
 * - count: number or string shown in an orange pill after the label
 * - onClick: renders a button; without it the chip is a plain span
 * - sx: extra styles, merged last
 */
const Chip = ({
  children,
  selected = false,
  solid = false,
  disabled = false,
  startIcon,
  endIcon,
  count,
  onClick,
  sx: sxOverride = {},
  ...rest
}) => {
  const Root = onClick ? ButtonBase : Box;
  const state =
    STATES[
      disabled
        ? selected
          ? "disabledSelected"
          : "disabled"
        : selected
          ? solid
            ? "selectedSolid"
            : "selected"
          : "default"
    ];

  return (
    <Root
      {...(onClick
        ? { onClick, disabled, "aria-pressed": selected }
        : { component: "span" })}
      {...rest}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        // Figma's 1px stroke sits inside the 32px height, so trim 1px from the padding
        padding: "7px 11px",
        borderRadius: "8px",
        boxSizing: "border-box",
        whiteSpace: "nowrap",
        margin: 0,
        backgroundColor: state.backgroundColor,
        border: `1px solid ${state.borderColor}`,
        color: state.color,
        fontFamily: '"Lexend", sans-serif',
        fontSize: 12,
        fontStyle: "normal",
        fontWeight: state.fontWeight,
        lineHeight: 1.35,
        letterSpacing: "0.15px",
        ...sxOverride,
      }}
    >
      {startIcon && (
        <Box component="span" aria-hidden="true" sx={iconSx(14)}>
          {startIcon}
        </Box>
      )}
      {children}
      {count !== undefined && count !== null && (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 16,
            height: 16,
            padding: "0 5px",
            boxSizing: "border-box",
            borderRadius: "999px",
            backgroundColor: state.countBg,
            color: state.countColor,
            fontSize: 11,
            fontWeight: 500,
          }}
        >
          {count}
        </Box>
      )}
      {endIcon && (
        <Box component="span" aria-hidden="true" sx={iconSx(12)}>
          {endIcon}
        </Box>
      )}
    </Root>
  );
};

export default Chip;
